import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';

// import { VirtualizedList } from './VirtualizedList';
import { LISTING_MIN_HEIGHT } from './constants';
import ListingState from './ListingState';
import { Skeleton } from '../skeleton';

import type { ColDef, ICellRendererParams } from 'ag-grid-community';

export type SortDirection = 'asc' | 'desc' | null;

interface SortState {
  field: string;
  direction: SortDirection;
}

interface TableViewProps<TData> {
  rows: TData[];
  columns: ColDef<TData>[];
  rowKey: (row: TData, index: number) => string | number;
  rowRenderer?: (row: TData) => React.ReactNode[];
  sortState?: SortState;
  onSortChange?: (next: SortState) => void;
  rowHeight?: number;
  height?: number;
  minHeight?: number;
  disableInternalScroll?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  emptyMessage?: string;
}

const getColumnTemplate = <TData,>(columns: ColDef<TData>[]) =>
  columns
    .map((column) => {
      if (column.width) {
        return `${column.width}px`;
      }
      const min = column.minWidth ?? 120;
      const flex = (column as any).flex ?? 1;
      const max = column.maxWidth ? `${column.maxWidth}px` : `${flex}fr`;
      return `minmax(${min}px, ${max})`;
    })
    .join(' ');

const getCellValue = <TData,>(row: TData, column: ColDef<TData>) => {
  if (column.valueGetter) {
    return column.valueGetter({ data: row });
  }
  if (column.field) {
    return (row as Record<string, unknown>)[column.field];
  }
  return undefined;
};

const renderCellContent = <TData,>(row: TData, column: ColDef<TData>) => {
  const rawValue = getCellValue(row, column);
  const formatted =
    column.valueFormatter?.({ value: rawValue, data: row }) ?? rawValue;

  if (column.cellRenderer) {
    const params: ICellRendererParams<TData> = {
      value: rawValue,
      data: row,
      colDef: column,
    };
    return column.cellRenderer(params);
  }

  if (formatted === null || formatted === undefined || formatted === '') {
    return '-';
  }

  return formatted as React.ReactNode;
};

export const TableView = <TData,>({
  rows,
  columns,
  rowKey,
  rowRenderer,
  sortState,
  onSortChange,
  rowHeight = 56,
  height,
  minHeight,
  disableInternalScroll = false,
  isLoading = false,
  isError = false,
  emptyMessage = 'No Data Available',
}: TableViewProps<TData>) => {
  const templateColumns = useMemo(() => getColumnTemplate(columns), [columns]);
  const resolvedMinHeight = minHeight ?? LISTING_MIN_HEIGHT;
  const resolvedHeight = height ?? resolvedMinHeight;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useLayoutEffect(() => {
    if (!headerRef.current) return;
    const updateHeight = () => {
      if (!headerRef.current) return;
      setHeaderHeight(headerRef.current.clientHeight);
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, []);

  // Reset scroll position to 0 on mount
  useLayoutEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0;
    }
    if (headerRef.current) {
      headerRef.current.scrollLeft = 0;
    }
  }, []);

  const bodyHeight = Math.max(0, resolvedHeight - headerHeight);

  const handleSort = (column: ColDef<TData>) => {
    if (!column.sortable || !column.field) return;
    const isSameField = sortState?.field === column.field;
    const nextDirection: SortDirection = isSameField
      ? sortState?.direction === 'asc'
        ? 'desc'
        : sortState?.direction === 'desc'
        ? null
        : 'asc'
      : 'asc';

    onSortChange?.({
      field: column.field,
      direction: nextDirection,
    });
  };

  const renderHeaderIcon = (column: ColDef<TData>) => {
    if (!column.sortable) {
      return null;
    }
    if (sortState?.field !== column.field || !sortState?.direction) {
      return <ArrowUpDown className="w-4 h-4 text-neutral-400" />;
    }
    return sortState.direction === 'asc' ? (
      <ArrowUp className="w-4 h-4 text-brand-600" />
    ) : (
      <ArrowDown className="w-4 h-4 text-brand-600" />
    );
  };

  const totalMinWidth = useMemo(() => {
    let sum = 0;
    columns.forEach((col) => {
      const w = col.width || col.minWidth || 120;
      sum += w;
    });
    // Remove the hardcoded 102rem minimum to allow the table to be responsive
    const remValue = sum / 16;
    return `max(100%, ${remValue}rem)`;
  }, [columns]);

  const handleContainerScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const scrollLeft = e.currentTarget.scrollLeft;
      if (headerRef.current) {
        headerRef.current.scrollLeft = scrollLeft;
      }
    },
    []
  );

  /*
  const handleVirtualizedScroll = React.useCallback(
    (_scrollTop: number, scrollLeft: number) => {
      if (headerRef.current) {
        headerRef.current.scrollLeft = scrollLeft;
      }
    },
    []
  );
  */

  if (isError) {
    return (
      <ListingState
        message="Unable to load records. Please try again."
        minHeight={resolvedMinHeight}
        variant="error"
      />
    );
  }

  if (!isLoading && rows.length === 0) {
    return (
      <ListingState message={emptyMessage} minHeight={resolvedMinHeight} />
    );
  }

  const renderRow = (row: TData, index: number) => {
    const customCells = rowRenderer?.(row);
    return (
      <div
        key={rowKey(row, index)}
        className="grid items-center border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors"
        style={{
          gridTemplateColumns: templateColumns,
          height: rowHeight,
          minWidth: totalMinWidth,
          width: totalMinWidth,
        }}
      >
        {columns.map((column, columnIndex) => (
          <div
            key={`${rowKey(row, index)}-${column.field ?? column.headerName}`}
            title={String(getCellValue(row, column) ?? '')}
            className={`px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300 min-w-0 overflow-hidden text-ellipsis ${
              column.field !== '__actions' ? 'whitespace-nowrap' : ''
            } ${column.cellClass ?? ''}`}
          >
            {customCells?.[columnIndex] ?? renderCellContent(row, column)}
          </div>
        ))}
      </div>
    );
  };

  const renderHeader = () => (
    <div
      ref={headerRef}
      className="overflow-hidden bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800"
    >
      <div
        className="grid items-center"
        style={{
          gridTemplateColumns: templateColumns,
          minWidth: totalMinWidth,
          width: totalMinWidth,
        }}
      >
        {columns.map((column) => {
          const headerContent =
            column.headerRenderer?.() ?? column.headerName ?? column.field;
          const contentNode =
            typeof headerContent === 'string' ? (
              <span className="text-neutral-500 dark:text-neutral-400">
                {headerContent}
              </span>
            ) : (
              headerContent
            );

          if (!column.sortable) {
            return (
              <div
                key={column.headerName ?? column.field}
                title={
                  typeof headerContent === 'string' ? headerContent : undefined
                }
                className={`px-4 py-3 text-left text-xs font-medium tracking-wider flex items-center gap-2 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap ${
                  column.headerClass ?? ''
                }`.trim()}
              >
                {contentNode}
              </div>
            );
          }

          return (
            <button
              key={column.headerName ?? column.field}
              type="button"
              onClick={() => handleSort(column)}
              title={
                typeof headerContent === 'string' ? headerContent : undefined
              }
              className={`px-4 py-3 text-left text-xs font-medium tracking-wider flex items-center gap-2 cursor-pointer min-w-0 overflow-hidden text-ellipsis whitespace-nowrap ${
                column.headerClass ?? ''
              }`.trim()}
            >
              {contentNode}
              {renderHeaderIcon(column)}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div
      className={`flex flex-col h-full min-h-0 min-w-0 w-full relative bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded-lg ${
        disableInternalScroll ? '' : 'overflow-hidden'
      }`}
      style={!disableInternalScroll ? { height: resolvedHeight } : undefined}
    >
      <div className="sticky top-0 z-30 bg-white dark:bg-neutral-950 shadow-sm">
        {renderHeader()}
      </div>

      <div
        ref={containerRef}
        onScroll={handleContainerScroll}
        className={`flex-1 min-h-0 overflow-x-auto custom-scroll ${
          disableInternalScroll ? 'overflow-y-visible' : 'overflow-y-hidden'
        }`}
      >
        {isLoading ? (
          <div
            className="divide-y divide-neutral-200 dark:divide-neutral-800 flex flex-col"
            style={!disableInternalScroll ? { height: bodyHeight } : undefined}
          >
            <div
              style={{ minWidth: totalMinWidth, width: totalMinWidth }}
              className="pb-8"
            >
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={`skeleton-row-${index}`}
                  className="grid items-center"
                  style={{
                    gridTemplateColumns: templateColumns,
                    height: rowHeight,
                  }}
                >
                  {columns.map((_, colIndex) => (
                    <div
                      key={`skeleton-cell-${index}-${colIndex}`}
                      className="px-4 py-3"
                    >
                      <Skeleton className="h-3 w-3/4" />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : disableInternalScroll ? (
          <div className="flex flex-col w-full pb-8">
            <div style={{ minWidth: totalMinWidth, width: totalMinWidth }}>
              {rows.map(renderRow)}
            </div>
          </div>
        ) : (
          <div
            className="flex flex-col w-full pb-8 overflow-y-auto"
            style={{
              height: bodyHeight,
              minWidth: totalMinWidth,
              width: totalMinWidth,
            }}
          >
            {rows.map((row, index) => renderRow(row, index))}
          </div>
        )}
      </div>
    </div>
  );
};
